import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { organizationFormSchema } from '@/lib/schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useScopeTypes } from '@/hooks/useScopeTypes';
import { useOrganizationSearch } from '@/hooks/useOrganizations';
import type { Organization, OrganizationFormData } from '@/lib/schemas';

interface OrganizationFormProps {
  mode: 'create' | 'edit';
  fieldSet?: 'minimal' | 'full';  // minimal: name, roles, email, phone | full: all fields
  initialData?: Organization;
  onSubmit: (data: OrganizationFormData) => void | Promise<void>;
  onCancel: () => void;
  children?: React.ReactNode;  // For extensibility (contacts section, etc.)
}

export function OrganizationForm({
  mode,
  fieldSet = 'full',
  initialData,
  onSubmit,
  onCancel,
  children
}: OrganizationFormProps) {
  // Fetch scope types for category dropdown (Phase 7.5: unified with project scopes)
  const { data: scopeTypes, isLoading: categoriesLoading } = useScopeTypes();

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch, control } = useForm<OrganizationFormData>({
    resolver: zodResolver(organizationFormSchema),
    defaultValues: {
      payment_terms: '30',
      // Phase 5 Batch 6 Phase 3: Default to client role for minimal fieldSet (inline creation from project form)
      is_client: fieldSet === 'minimal' ? true : false,
      is_consultant: false,
      is_government: false,
      category_id: '', // Phase 6 Batch 2: Empty string keeps Select controlled
    },
  });

  // Phase 7 Batch 1.2: Real-time duplicate name validation
  const [debouncedName, setDebouncedName] = useState('');
  const watchedName = watch('name');

  // Debounce name input for duplicate check
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedName(watchedName || '');
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [watchedName]);

  // Check for duplicate names (only in create mode)
  const { data: duplicateCheck } = useOrganizationSearch(
    debouncedName,
    undefined,
    { enabled: mode === 'create' && debouncedName.length > 0 }
  );

  // Check if there's an exact match (case-insensitive)
  const hasDuplicateName = mode === 'create' && duplicateCheck?.organizations?.some(
    org => org.name.toLowerCase() === debouncedName.toLowerCase()
  );

  // Load initialData for edit mode
  // Wait for categories to load before resetting form to prevent "uncontrolled to controlled" warning
  // Pattern from ProjectEditPage.tsx:70-81 - simpler approach without ref
  useEffect(() => {
    if (mode === 'edit' && initialData && !categoriesLoading) {
      reset({
        name: initialData.name,
        is_client: initialData.is_client || false,
        is_consultant: initialData.is_consultant || false,
        is_government: initialData.is_government || false,
        legal_name: initialData.legal_name || undefined,
        email: initialData.email || undefined,
        phone: initialData.phone || undefined,
        address: initialData.address || undefined,
        billing_address: initialData.billing_address || undefined,
        notes: initialData.notes || undefined,
        payment_terms: initialData.payment_terms?.toString() || '30',
        category_id: initialData.category_id || '', // Phase 6 Batch 2: Empty string keeps Select controlled
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, mode, categoriesLoading]); // reset is stable, don't include in deps

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Client Name */}
      <div>
        <Label htmlFor="name">Client Name *</Label>
        <Input
          id="name"
          {...register('name')}
          placeholder="e.g., ACME Corporation"
          className={hasDuplicateName ? 'border-destructive' : ''}
        />
        {errors.name && (
          <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
        )}
        {hasDuplicateName && (
          <p className="text-sm text-destructive mt-1">
            ⚠️ A client with this name already exists. Please use a different name.
          </p>
        )}
      </div>

      {/* Client Roles */}
      <div>
        <Label>Client Roles *</Label>
        <div className="space-y-3 mt-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_client"
              checked={watch('is_client')}
              onCheckedChange={(checked) => setValue('is_client', checked as boolean)}
            />
            <label
              htmlFor="is_client"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Client - Can be assigned as project client
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_consultant"
              checked={watch('is_consultant')}
              onCheckedChange={(checked) => setValue('is_consultant', checked as boolean)}
            />
            <label
              htmlFor="is_consultant"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Consultant - Provides consulting services
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_government"
              checked={watch('is_government')}
              onCheckedChange={(checked) => setValue('is_government', checked as boolean)}
            />
            <label
              htmlFor="is_government"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Government - Government agency or entity
            </label>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          At least one role must be selected.
        </p>
        {errors.is_client && (
          <p className="text-sm text-destructive mt-1">{errors.is_client.message}</p>
        )}
      </div>

      {/* Client Category (Phase 7.5: Uses Scope Types) */}
      <div>
        <Label htmlFor="category_id">Category</Label>
        <Controller
          name="category_id"
          control={control}
          render={({ field }) => (
            <Select
              key={field.value || 'empty'} // Force remount when value changes (pattern from ProjectEditPage:253)
              value={field.value}
              onValueChange={field.onChange}
              disabled={categoriesLoading}
            >
              <SelectTrigger id="category_id">
                <SelectValue placeholder={categoriesLoading ? 'Loading types...' : 'Select type (optional)'} />
              </SelectTrigger>
              <SelectContent>
                {scopeTypes?.map((scopeType) => (
                  <SelectItem key={scopeType.id} value={scopeType.id}>
                    {scopeType.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Client type (e.g., Structural Engineer, Architect). Managed in Settings → Client Types.
        </p>
      </div>

      {/* Legal Name (full only) */}
      {fieldSet === 'full' && (
        <div>
          <Label htmlFor="legal_name">Legal Entity Name</Label>
          <Input
            id="legal_name"
            {...register('legal_name')}
            placeholder="e.g., ACME Corporation LLC"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Default legal name for contracts and invoicing. DBAs can have their own legal names that override this on invoices.
          </p>
        </div>
      )}

      {/* Phase 9.14: Hidden - Business Email/Phone not useful, default to primary contact instead
      <div>
        <Label htmlFor="email">Business Email</Label>
        <Input
          id="email"
          type="email"
          {...register('email')}
          placeholder="e.g., contact@acme.com"
        />
        {errors.email && (
          <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="phone">Business Phone</Label>
        <Input
          id="phone"
          type="tel"
          {...register('phone')}
          placeholder="e.g., (555) 123-4567"
        />
      </div>
      */}

      {/* Full fieldset: Address, Billing Address, Notes, Payment Terms */}
      {fieldSet === 'full' && (
        <>
          {/* Address */}
          <div>
            <Label htmlFor="address">Address</Label>
            <textarea
              id="address"
              {...register('address')}
              placeholder="Full mailing address"
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {/* Billing Address */}
          <div>
            <Label htmlFor="billing_address">Billing Address</Label>
            <textarea
              id="billing_address"
              {...register('billing_address')}
              placeholder="Billing address (leave blank if same as address)"
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes</Label>
            <textarea
              id="notes"
              {...register('notes')}
              placeholder="Relationship notes, preferences, etc."
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {/* Payment Terms */}
          <div>
            <Label htmlFor="payment_terms">Payment Terms (days)</Label>
            <Input
              id="payment_terms"
              type="number"
              {...register('payment_terms')}
              placeholder="30"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Default payment terms in days (e.g., 30 for Net 30)
            </p>
          </div>
        </>
      )}

      {children}  {/* For contacts section, etc. */}

      <div className="flex gap-4">
        <Button
          type="submit"
          disabled={hasDuplicateName}
        >
          {mode === 'create' ? 'Create Client' : 'Save Changes'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
